import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { hotAnime } from '@/mocks/anime';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const anime = hotAnime.find((a) => a.slug === id) || hotAnime[0];

  const chapters = Array.from({ length: 12 }, (_, i) => ({
    num: i + 1,
    title: `第${i + 1}话 - ${['开端', '觉醒', '试炼', '激战', '转折', '成长', '考验', '突破', '对决', '真相', '抉择', '新篇'][i]}`,
  }));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${anime.title} - 51动漫在线观看`,
        description: `${anime.title}是51动漫平台热门的${anime.category}作品，评分${anime.rating}分，已更新至${anime.updateTo}。下拉式阅读体验，高清画质免费观看。`,
        author: { '@type': 'Organization', name: '51动漫' },
        publisher: { '@type': 'Organization', name: '51动漫' },
        datePublished: '2025-01-01',
        dateModified: '2026-08-04',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51dongman.com' },
          { '@type': 'ListItem', position: 2, name: anime.category, item: `https://www.51dongman.com/category/${anime.category === '日漫' ? 'riman' : anime.category === '韩漫' ? 'hanman' : 'guoman'}` },
          { '@type': 'ListItem', position: 3, name: anime.title },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <main>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <Navbar />
        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: anime.category, href: `/category/${anime.category === '日漫' ? 'riman' : anime.category === '韩漫' ? 'hanman' : 'guoman'}` },
              { label: anime.title },
            ]}
          />

          <section className="py-8 md:py-12 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
                <div className="flex-shrink-0 w-full sm:w-56 mx-auto md:mx-0">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden border border-background-200/70">
                    <img
                      src={anime.imageUrl}
                      alt={anime.title}
                      title={`${anime.title}封面 - 51动漫`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">{anime.title}</h1>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">{anime.category}</span>
                    <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">评分 {anime.rating}</span>
                    <span className="text-foreground-500 text-sm">{anime.updateTo}</span>
                  </div>
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                    {anime.title}是51动漫平台热门的{anime.category}作品，累计评分{anime.rating}分，深受广大动漫爱好者喜爱。本作品拥有精美的画风和引人入胜的剧情，已更新至{anime.updateTo}。51动漫提供高清画质和下拉式阅读体验，让您畅享每一话的精彩内容。
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button className="flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm transition-all cursor-pointer whitespace-nowrap">
                      <i className="ri-play-circle-line text-lg"></i>
                      开始阅读第一话
                    </button>
                    <button className="flex items-center gap-2 px-6 py-3 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 font-medium rounded-full text-sm transition-all cursor-pointer whitespace-nowrap">
                      <i className="ri-bookmark-line text-lg"></i>
                      加入收藏
                    </button>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="font-heading text-xl text-foreground-950 mb-4">章节列表</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {chapters.map((ch) => (
                    <div
                      key={ch.num}
                      className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer"
                    >
                      <span className="text-foreground-900 text-sm font-medium">{ch.title}</span>
                      <i className="ri-arrow-right-s-line text-foreground-500"></i>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-100 rounded-xl border border-background-200/70 p-6 md:p-8">
                <h2 className="font-heading text-xl text-foreground-950 mb-6 text-center">下拉式阅读体验区</h2>
                <div className="space-y-6">
                  {[1, 2, 3, 4, 5].map((page) => (
                    <div key={page} className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-background-200/70">
                        <span className="text-foreground-600 text-sm">第1话 - 第{page}页</span>
                        <span className="text-foreground-500 text-xs">下拉继续阅读</span>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-background-300 rounded-full w-full"></div>
                        <div className="h-3 bg-background-300 rounded-full w-5/6"></div>
                        <div className="h-3 bg-background-300 rounded-full w-4/6"></div>
                        <div className="h-3 bg-background-300 rounded-full w-full"></div>
                        <div className="h-3 bg-background-300 rounded-full w-3/4"></div>
                        <div className="h-3 bg-background-300 rounded-full w-5/6"></div>
                        <div className="h-3 bg-background-300 rounded-full w-2/3"></div>
                        <div className="h-3 bg-background-300 rounded-full w-full"></div>
                        <div className="h-3 bg-background-300 rounded-full w-4/5"></div>
                        <div className="h-3 bg-background-300 rounded-full w-3/5"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </main>
    </>
  );
}